import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, BaggageClaim, ImageIcon, Minus, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'react-toastify';
import { isHttpError } from '@/api/custom-client';
import type { SalesOrderItemDetail, StandardizedError } from '@/api/hanly-v2/hanlyAPIV2.schemas';
import {
  getSalesOrderDetailQueryKey,
  useSalesOrderDetail,
  useSalesOrderShipmentCreate,
} from '@/api/hanly-v2/sales-orders/sales-orders';
import { getCdnImageUrl } from '@/utils/image';

const ItemCard = ({
  item,
  pickingQty,
  onPickingQtyChange,
}: {
  item: SalesOrderItemDetail;
  pickingQty?: number;
  onPickingQtyChange?: (qty: number) => void;
}) => {
  const totalQty = Number(item.quantity ?? 0);
  const shippedQty = item.shipment ? totalQty : 0;
  const remainingQty = totalQty - shippedQty;
  const isShipped = item.shipment !== null && item.shipment !== undefined;

  return (
    <div className="bg-white flex gap-2 items-start p-2">
      {item.thumbnail_url ? (
        <img
          src={getCdnImageUrl(item.thumbnail_url, { width: 160, height: 160 })}
          alt=""
          className="size-20 shrink-0 rounded border border-gray-200 object-cover"
          loading="lazy"
        />
      ) : (
        <div className="size-20 shrink-0 rounded border border-gray-200 flex items-center justify-center bg-gray-100">
          <ImageIcon className="size-6 text-gray-400" />
        </div>
      )}
      <div className="flex-1 flex flex-col min-w-0">
        <p>{item.name}</p>
        <p className="text-sm text-neutral-500">{[item.product_code, item.spec].filter(Boolean).join('/')}</p>
      </div>

      <div className="shrink-0 w-[120px] flex flex-col items-center gap-1">
        {!isShipped && onPickingQtyChange && (
          <>
            <button
              type="button"
              onClick={() => onPickingQtyChange(remainingQty)}
              className="text-sky-800 text-right w-full cursor-pointer"
            >
              全てピッキング
            </button>
            <div className="flex items-center border border-neutral-400 rounded overflow-hidden w-full">
              <button
                type="button"
                onClick={() => onPickingQtyChange(Math.max(0, (pickingQty ?? 0) - 1))}
                className="bg-green-600 text-white size-7 flex items-center justify-center shrink-0 cursor-pointer"
              >
                <Minus className="size-4" />
              </button>
              <span className="flex-1 text-center text-base">{pickingQty ?? 0}</span>
              <button
                type="button"
                onClick={() => onPickingQtyChange(Math.min(remainingQty, (pickingQty ?? 0) + 1))}
                className="bg-green-600 text-white size-7 flex items-center justify-center shrink-0 cursor-pointer"
              >
                <Plus className="size-4" />
              </button>
            </div>
          </>
        )}
        <div className="flex gap-2 items-center">
          <BaggageClaim className="size-6 shrink-0 text-neutral-500 stroke-1" />
          <p className="text-xl">
            {shippedQty}/{totalQty}
          </p>
        </div>
      </div>
    </div>
  );
};

export const PickingDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const orderId = Number(id);
  const isValidOrderId = Number.isInteger(orderId) && orderId > 0;
  const { data: order, isLoading } = useSalesOrderDetail(orderId, {
    query: { enabled: isValidOrderId },
  });
  const [pickingQuantities, setPickingQuantities] = useState<Record<number, number>>({});
  const queryClient = useQueryClient();
  const { mutate: createShipment, isPending: isShipping } = useSalesOrderShipmentCreate();

  const { unshippedItems, shippedItems } = useMemo(() => {
    if (!order) return { unshippedItems: [], shippedItems: [] };
    const unshipped: SalesOrderItemDetail[] = [];
    const shipped: SalesOrderItemDetail[] = [];
    for (const item of order.items) {
      if (item.shipment !== null && item.shipment !== undefined) {
        shipped.push(item);
      } else {
        unshipped.push(item);
      }
    }
    return { unshippedItems: unshipped, shippedItems: shipped };
  }, [order]);

  const updatePickingQty = (itemId: number, qty: number) => {
    setPickingQuantities((prev) => ({ ...prev, [itemId]: qty }));
  };

  const handleShipmentComplete = () => {
    const itemsToShip = unshippedItems.filter((item) => (pickingQuantities[item.id] ?? 0) > 0);
    if (itemsToShip.length === 0) return;

    const salesOrderItemIds = itemsToShip.map((item) => item.id);
    const itemQuantities: Record<string, string> = {};
    let hasPartial = false;
    for (const item of itemsToShip) {
      const pickingQty = pickingQuantities[item.id] ?? 0;
      const totalQty = Number(item.quantity ?? 0);
      if (pickingQty !== totalQty) {
        hasPartial = true;
        itemQuantities[String(item.id)] = String(pickingQty);
      }
    }

    createShipment(
      {
        data: {
          sales_order_item_ids: salesOrderItemIds,
          // ピッキング完了は実際に出荷した時刻を記録する
          // (未指定にするとAPI側で出荷予定日が既定値になるため明示的に送る)
          shipped_at: new Date().toISOString(),
          ...(hasPartial ? { item_quantities: itemQuantities } : {}),
        },
      },
      {
        onSuccess: () => {
          setPickingQuantities({});
          queryClient.invalidateQueries({ queryKey: getSalesOrderDetailQueryKey(orderId) });
        },
        // 期限切れのロットなど出荷できない理由は API の 400 に入る(この画面では出荷の許可を出せない)
        onError: (error) => {
          const detail = isHttpError<StandardizedError>(error) ? error.data?.errors?.[0]?.detail : undefined;
          toast.error(detail ?? '出荷できませんでした');
        },
      },
    );
  };

  const hasPickedItems = unshippedItems.some((item) => (pickingQuantities[item.id] ?? 0) > 0);

  if (isLoading) {
    return <p className="text-sm text-neutral-500 text-center py-4">読み込み中...</p>;
  }

  if (!order) {
    return <p className="text-sm text-neutral-500 text-center py-4">受注が見つかりません</p>;
  }

  const customerName =
    typeof order.supplier_partner_customer === 'object' ? order.supplier_partner_customer?.name : '';

  return (
    <div className="flex flex-col min-h-[calc(100vh-44px)] gap-3">
      {/* ヘッダー */}
      <div className="bg-white flex gap-2 items-center px-2 py-1">
        <Link to="/picking" className="shrink-0">
          <ArrowLeft className="size-5" />
        </Link>
        <div className="flex flex-col min-w-0">
          <p className="text-sm">受注No.{order.id}</p>
          {customerName && <p className="-mt-1">{customerName}</p>}
        </div>
      </div>

      {/* コンテンツ */}
      <div className="flex-1 flex flex-col gap-2 pb-3 px-5">
        {/* 未出荷 */}
        {unshippedItems.length > 0 && (
          <>
            <h3 className="border-b border-neutral-400 pb-1">未出荷</h3>
            <div className="flex flex-col gap-2">
              {unshippedItems.map((item) => (
                <ItemCard
                  key={item.id}
                  item={item}
                  pickingQty={pickingQuantities[item.id] ?? 0}
                  onPickingQtyChange={(qty) => updatePickingQty(item.id, qty)}
                />
              ))}
            </div>
          </>
        )}

        {/* 出荷済 */}
        {shippedItems.length > 0 && (
          <>
            <h3 className="border-b border-neutral-400 pb-1 mt-2">出荷済</h3>
            <div className="flex flex-col gap-2">
              {shippedItems.map((item) => (
                <ItemCard key={item.id} item={item} />
              ))}
            </div>
          </>
        )}
      </div>

      {/* フッターボタン */}
      {unshippedItems.length > 0 && (
        <div className="sticky bottom-0 bg-white flex gap-3 px-5 py-2">
          <button
            type="button"
            disabled={!hasPickedItems || isShipping}
            onClick={handleShipmentComplete}
            className={`flex-1 py-2 text-lg font-bold text-white ${
              hasPickedItems && !isShipping ? 'bg-green-600' : 'bg-gray-400 opacity-50'
            }`}
          >
            {isShipping ? '処理中...' : '出荷完了'}
          </button>
        </div>
      )}
    </div>
  );
};
